import { z } from 'zod';
import { RecordId } from '@itc-mod/supply/shared';
import { Donation, DonationActions, People } from './schemas.ts';

// What a donation can be for (plan 4B): generic, so Donations knows nothing of who owns one.

export const BuildLine = z.object({ material: z.string(), quantity: z.number().int() });
export type BuildLine = z.infer<typeof BuildLine>;

/** `saveTarget`: the whole target, each time. `id` is the owner's own record id. */
export const SaveTargetInput = z.object({
  id: RecordId,
  title: z.string().trim().min(1).max(200),
  link: z.string().max(200),
  /** Who may accept, as well as holders of `managePermission` (T1). */
  receivers: z.array(z.string().max(15)).max(20),
  managePermission: z.string().max(64),
  /** 100, or 150 for a corp build. */
  creditPercent: z.number().int().min(1).max(1000),
  selfCredit: z.boolean(),
  creator: z.string().max(15),
  /** Taking pledges. */
  open: z.boolean(),
  /** The line that means one finished build, and what it's made of. */
  buildName: z.string().max(200),
  buildLines: z.array(BuildLine).max(40),
});
export type SaveTargetInput = z.infer<typeof SaveTargetInput>;
export const SaveTargetResult = z.object({ ok: z.literal(true) });

export const TargetView = z.object({ id: z.string(), title: z.string(), link: z.string(), open: z.boolean(), buildName: z.string() });
export type TargetView = z.infer<typeof TargetView>;

/** T3: a target's donations as one viewer may see them. */
export const TargetDonations = z.object({
  target: TargetView,
  items: z.array(z.object({ donation: Donation, actions: DonationActions })),
  people: People,
  /** The viewer may accept here (T1). */
  decides: z.boolean(),
});
export type TargetDonations = z.infer<typeof TargetDonations>;

/** What a target's progress needs: amounts by material id, or `build` for finished builds. */
export const TargetSummary = z.object({
  id: z.string(),
  received: z.record(z.string(), z.number()),
  pledged: z.record(z.string(), z.number()),
  /** Donations of any status, for the rule that a project with any can't be deleted. */
  donations: z.number().int(),
  contributors: z.array(z.object({ key: z.string(), userId: z.string().nullable(), name: z.string(), value: z.number().int() })),
});
export type TargetSummary = z.infer<typeof TargetSummary>;

export const SummariesInput = z.object({ ids: z.array(z.string().max(15)).max(500).nullable() });
export const SummariesResult = z.object({ targets: z.array(TargetSummary) });

/** The key a line counts under. */
export const BUILD_KEY = 'build';
